import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
} from '@floating-ui/dom'
import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import {
  customElement,
  property,
  query,
  queryAssignedElements,
  state,
} from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { watch } from '../../common/decorators'
import {
  FloatingStrategy,
  LinkTarget,
  PopupPlacement,
  TabNavigationMode,
} from '../../common/types'
import { DdsElement } from '../../internal/dds-hu-element'
import { SizedMixin } from '../../internal/mixin/sizedMixin'
import popupStyles from '../popup/popup.scss?inline'
import styles from './navigation-menu-item.scss?inline'
import { SubmenuController } from './submenu-controller'

/**
 * `dap-ds-navigation-menu-item`
 * @summary A navigation menu item that can contain either a simple link or dropdown content.
 * Supports nested items with cascading flyout behavior. Part of the navigation menu system.
 * This is for NAVIGATION (website menus), not application menus.
 *
 * @element dap-ds-navigation-menu-item
 * @title - Navigation Menu Item
 * @group navigation-menu
 *
 * @property {string} label - The label text for the navigation item. This will be displayed in the title slot if no custom title slot content is provided.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the popup. Default is 'sm'.
 * @property {boolean} disabled - The disabled state of the popup. Default is false.
 * @property {boolean} opened - The open state of the popup. Default is false.
 * @property {'top' | 'right' | 'bottom' | 'left' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end'} placement - The placement of the popup (automatically adjusted based on nesting level). Default is 'bottom-start'.
 * @property {'absolute' | 'fixed'} floatingStrategy - The floating strategy of the popup. Default is 'fixed'.
 * @property {number} offset - The offset of the popup. Default is 0.
 * @property {boolean} sync - Whether the popup should sync its width with the trigger. Default is false.
 * @property {number | 'auto'} maxHeight - The maximum height of the popup. Default is 250.
 * @property {number | 'auto'} maxWidth - The maximum width of the popup. Default is 'auto'.
 * @property {boolean} hasArrow - Whether the popup has an arrow. Default is false.
 * @property {boolean} overflow - Whether the popup should overflow. Default is true.
 * @property {boolean} fullWidth - Whether the popup should take full width of the screen. Default is false.
 * @property {boolean} exactHref - Whether the navigation item should be active if the href is exactly the same as the activeHref. Default is false.
 * @property {string} icon - The name of the icon to display in the trigger.
 * @property {string} ariaLabelledBy - The name of the element that labels the navigation dropdown.
 * @property {string} activeHref - The href of the navigation item that is active (receives aria-current="page").
 * @property {string} baseHref - The href of the navigation item. If provided, the navigation item will be active if the href is a substring of the activeHref.
 * @property {string} href - The href URL for the navigation item link. When provided, the default trigger renders as a link.
 * @property {'_blank' | '_self' | '_parent' | '_top'} target - The target attribute for link navigation.
 * @property {string} rel - The rel attribute for link security and behavior.
 * @property {number} level - The nesting level of this navigation item (0 = top-level, automatically set for nested items). Default is 0.
 * @property {'horizontal' | 'vertical'} orientation - The orientation of the parent navigation menu (automatically inherited). Default is 'horizontal'.
 * @property {'group' | 'items'} tabMode - Controls how Tab key navigates through top-level items. Default is 'group'.
 *
 * @event {{ href: string, event: Event }} dds-navigation-item-click - Fired when a navigation item is clicked.
 * @event {{ item: DapDSNavigationMenuItem }} dds-navigation-dropdown-open - Fired when a navigation dropdown is opened.
 *
 * @slot trigger - The trigger element (link, button, etc.) for this navigation item.
 * @slot title - The title of the navigation item.
 * @slot indicator - The expand indicator icon (defaults to arrow icon based on orientation and level).
 * @slot link - A router link element (e.g. Next.js Link) used for SPA navigation.
 *   Rendered outside the popup so it is always accessible to programmatic clicks.
 * @slot - The dropdown content (can contain nested dap-ds-navigation-menu-item elements).
 *
 * @csspart base - The base part of the navigation item.
 * @csspart trigger - The trigger element part.
 * @csspart trigger-base - The base part of the trigger button (from dap-ds-button).
 * @csspart trigger-content - The content part of the trigger button (from dap-ds-button).
 * @csspart trigger-high-contrast - The high contrast part of the trigger button (from dap-ds-button).
 * @csspart trigger-stack-base - The base part of the trigger stack (from dap-ds-stack).
 * @csspart title - The title slot wrapper.
 * @csspart indicator - The expand indicator container.
 * @csspart indicator-icon - The expand indicator icon part.
 * @csspart indicator-icon-base - The base part of the indicator icon (from dap-ds-icon).
 * @csspart indicator-icon-icon - The icon part of the indicator icon (from dap-ds-icon).
 * @csspart popup - The dropdown popup container.
 * @csspart content - The dropdown content wrapper (default slot).
 * @csspart arrow - The dropdown arrow part.
 *
 * @cssproperty --dds-navigation-menu-item-spacing - The padding/spacing of the navigation menu item content. (default: var(--dds-spacing-200)).
 * @cssproperty --dds-navigation-menu-item-border-width - The border width of the navigation menu item content. (default: var(--dds-border-width-base)).
 * @cssproperty --dds-navigation-menu-item-border-color - The border color of the navigation menu item content. (default: var(--dds-border-neutral-subtle)).
 * @cssproperty --dds-navigation-menu-item-border-radius - The border radius of the navigation menu item content. (default: var(--dds-radius-base)).
 * @cssproperty --dds-navigation-menu-item-background - The background color of the navigation menu item content. (default: var(--dds-background-neutral-base)).
 * @cssproperty --dds-navigation-menu-item-shadow - The box shadow of the navigation menu item content. (default: var(--dds-shadow-base)).
 *
 * @accessibility
 * - Active navigation items marked with `aria-current="page"`
 * - Dropdowns use `aria-expanded` and `aria-haspopup`
 * - No menu/menuitem roles (this is navigation, not an application menu)
 */
@customElement('dap-ds-navigation-menu-item')
export default class DapDSNavigationMenuItem extends SizedMixin(DdsElement) {
  static readonly styles = unsafeCSS(`${styles}${popupStyles}`)

  /** @ignore */
  @query('.popup') readonly popup!: HTMLDivElement

  /** @ignore */
  @queryAssignedElements({ slot: 'trigger' })
  private readonly _assignedTrigger!: HTMLElement[]

  // Popup properties
  /** @ignore */
  protected triggerElement: Element | null = null
  private cleanup: ReturnType<typeof autoUpdate> | undefined
  public readonly submenuController: SubmenuController

  /** The disabled state of the menu item. */
  @property({ reflect: true, type: Boolean }) disabled = false

  /** The open state of the dropdown menu. */
  @property({ reflect: true, type: Boolean }) opened = false

  /** The placement of the dropdown. */
  @property({ reflect: true }) placement: PopupPlacement = 'bottom-start'

  /** The floating strategy of the dropdown. */
  @property({ reflect: true }) floatingStrategy: FloatingStrategy = 'fixed'

  /** The offset of the dropdown from the trigger. */
  @property({ type: Number }) offset = 0

  /** Whether the dropdown should sync its width with the trigger. */
  @property({ type: Boolean }) sync = false

  /** The maximum height of the dropdown. */
  @property({ type: Number }) maxHeight: number | 'auto' = 250

  /** The maximum width of the dropdown. */
  @property({ type: Number }) maxWidth: number | 'auto' = 'auto'

  /** Whether the dropdown has an arrow. */
  @property({ type: Boolean }) hasArrow = false

  /** Whether the dropdown should overflow. */
  @property({ type: Boolean }) overflow = true

  /** Whether the dropdown should take full width of the screen. */
  @property({ type: Boolean }) fullWidth = false

  /** The name of the icon to display in the trigger. */
  @property({ type: String, reflect: true }) icon?: string

  /** ID of element that labels the navigation dropdown. */
  @property({ type: String, attribute: 'aria-labelledby' })
  ariaLabelledBy?: string

  /** The href of the navigation item that is active. */
  @property({ type: String, reflect: true }) activeHref?: string

  /** The href of the navigation item for prefix matching. */
  @property({ type: String, reflect: true }) baseHref?: string

  /** Whether the navigation item should use exact href matching. */
  @property({ type: Boolean, reflect: true }) exactHref = false

  /** The href URL for the navigation item link. When provided, the default trigger renders as a link. */
  @property({ type: String, reflect: true }) href?: string

  /** The target attribute for link navigation. */
  @property({ type: String, reflect: true }) target?: LinkTarget

  /** The rel attribute for link security and behavior. */
  @property({ type: String, reflect: true }) rel?: string

  /** The label text for the navigation item, displayed in the title slot when no slot content is provided. */
  @property({ type: String }) label?: string

  /** The nesting level of this navigation item (0 = top-level). */
  @property({ type: Number, reflect: true }) level = 0

  /** The orientation of the parent navigation menu. */
  @property({ reflect: true }) orientation: 'horizontal' | 'vertical' =
    'horizontal'

  /** Controls tab key behavior (propagated from parent navigation menu). */
  @property({ type: String }) tabMode: TabNavigationMode = 'group'

  @state() hasContent = false
  @state() private hasLink = false
  @state() private hasTitleSlot = false
  @state() private _titleText = ''
  @state() private active = false
  @state() private openedByHover = false
  @state() private _isRovingActive = false

  /** Computed property to detect if this item contains nested navigation menu items. */
  get hasNestedItems(): boolean {
    const nestedItems = this.querySelectorAll('dap-ds-navigation-menu-item')
    return nestedItems.length > 0
  }

  private get _trigger(): HTMLElement[] {
    // If there are assigned elements in the trigger slot, use only DIRECT children
    if (this._assignedTrigger && this._assignedTrigger.length > 0) {
      const directTriggers = this._assignedTrigger.filter(
        el => el.parentElement === this,
      )
      if (directTriggers.length > 0) {
        return directTriggers
      }
    }
    // Otherwise, find the default button in the shadow DOM
    // When slot has no assigned content, the fallback content (button) is rendered
    // The button is rendered as part of the slot's fallback content in the shadow DOM
    const defaultButton = this.shadowRoot?.querySelector('dap-ds-button')
    return defaultButton ? [defaultButton as HTMLElement] : []
  }

  /** Get the element slotted into the `link` slot, if any. */
  private _getLinkElement(): HTMLElement | null {
    const slot = this.shadowRoot?.querySelector(
      'slot[name="link"]',
    ) as HTMLSlotElement
    if (!slot) return null
    const elements = slot.assignedElements()
    return elements.length > 0 ? (elements[0] as HTMLElement) : null
  }

  /** Handle slot change for the `link` slot. */
  private _handleLinkSlotChange = (e: Event) => {
    const slot = e.target as HTMLSlotElement
    if (slot) {
      const elements = slot.assignedElements()
      this.hasLink = elements.length > 0
      // Remove link element from tab order — the trigger is the tab stop
      elements.forEach(el => el.setAttribute('tabindex', '-1'))
    }
    this._updateActiveState()
  }

  /** Handle slot change for the `title` slot. */
  private _handleTitleSlotChange = (e: Event) => {
    const slot = e.target as HTMLSlotElement
    if (slot) {
      const nodes = slot.assignedNodes({ flatten: true })
      // Check if slot has any content (elements or text nodes)
      this.hasTitleSlot = nodes.some(
        node =>
          (node.nodeType === Node.ELEMENT_NODE ||
            (node.nodeType === Node.TEXT_NODE &&
              node.textContent?.trim().length)) &&
          node !== slot,
      )
      // Extract text for aria-label on the trigger button. Screen readers may
      // not traverse the multi-level slot chain (dap-ds-button > dap-ds-stack >
      // nav-item slot > title slot), so we provide an explicit label.
      this._titleText = nodes
        .map(n => n.textContent?.trim() ?? '')
        .join(' ')
        .trim()
    }
  }

  constructor() {
    super()
    this.submenuController = new SubmenuController(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('click', this._handleClick)
    this.addEventListener('mouseover', this._handleMouseOver)
    this.addEventListener('mouseleave', this._handleMouseLeave)
    this.addEventListener('keydown', this._handleKeyDown)
    this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this)
    this._updatePlacementForLevel()
    this.startPositionUpdate()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this._handleClick)
    this.removeEventListener('mouseover', this._handleMouseOver)
    this.removeEventListener('mouseleave', this._handleMouseLeave)
    this.removeEventListener('keydown', this._handleKeyDown)
    document.removeEventListener('mousedown', this.handleDocumentMouseDown)
    this.stopPositionUpdate()
  }

  async firstUpdated(changedProperties: Map<string, unknown>) {
    super.firstUpdated(changedProperties)
    this._updateNestedItems()
    // Ensure trigger element is initialized even if slotchange didn't fire
    this._handleTriggerSlotChangeCallback()
  }

  @watch('opened')
  handleOpenedChange() {
    if (this.opened && (this.hasNestedItems || this.hasContent)) {
      this._applyZIndex()
      this.updateComplete.then(() => {
        this.updatePosition()
        // Update safe triangle coordinates after position update
        this.submenuController.updateSafeTriangle()
      })
    }
  }

  @watch('activeHref')
  handleActiveHrefChange() {
    this._updateActiveState()
    this._updateNestedItems()
  }

  @watch('href')
  handleHrefChange() {
    this._updateActiveState()
  }

  @watch(['level', 'orientation'])
  handleLayoutChange() {
    this._updateNestedItems()
    this._updatePlacementForLevel()
  }

  /** Update nested navigation menu items with current properties. */
  private _updateNestedItems() {
    const allNestedItems = this.querySelectorAll(
      'dap-ds-navigation-menu-item',
    ) as NodeListOf<DapDSNavigationMenuItem>

    const nestedItems = Array.from(allNestedItems).filter(item => {
      const parentNavItem = item.parentElement?.closest(
        'dap-ds-navigation-menu-item',
      ) as DapDSNavigationMenuItem | null
      return parentNavItem === this
    })

    nestedItems.forEach(item => {
      item.level = this.level + 1
      item.orientation = this.orientation
      if (this.activeHref) {
        item.activeHref = this.activeHref
      }
    })
  }

  /** Update placement based on nesting level and orientation. */
  private _updatePlacementForLevel() {
    if (this.level === 0) {
      this.placement =
        this.orientation === 'horizontal' ? 'bottom-start' : 'right-start'
    } else {
      this.placement = 'right-start'
    }
  }

  /** Calculate z-index based on nesting level. */
  private _calculateZIndex(): number {
    const baseZIndex = 1000
    return baseZIndex + this.level * 10
  }

  /** Apply z-index based on nesting level. */
  private _applyZIndex() {
    if (this.popup) {
      const zIndex = this._calculateZIndex()
      this.popup.style.zIndex = String(zIndex)
    }
  }

  /** Start automatic position updates using Floating UI. */
  private startPositionUpdate() {
    if (!this.triggerElement || !this.popup) return

    this.cleanup = autoUpdate(this.triggerElement, this.popup, () => {
      this.updatePosition()
    })
  }

  /** Stop automatic position updates. */
  private stopPositionUpdate() {
    if (this.cleanup) {
      this.cleanup()
      this.cleanup = undefined
    }
  }

  /** Update popup position using Floating UI. */
  async updatePosition() {
    if (!this.triggerElement || !this.popup) return

    // For fixed positioning, use viewport as boundary to allow popups to escape constrained containers
    // For absolute positioning, use the navigation menu container as boundary
    const boundary =
      this.floatingStrategy === 'fixed'
        ? undefined // undefined means viewport for fixed positioning
        : this.closest('dap-ds-navigation-menu') || undefined

    const { x, y } = await computePosition(this.triggerElement, this.popup, {
      placement: this.placement,
      strategy: this.floatingStrategy,
      middleware: [
        offset(this.offset),
        flip({
          boundary: boundary,
        }),
        shift({
          padding: this.fullWidth ? 0 : 8,
          boundary: boundary,
        }),
        size({
          apply: ({ availableHeight, rects }) => {
            if (this.sync) {
              this.popup.style.width = `${rects.reference.width}px`
            }
            if (this.maxHeight !== 'auto') {
              this.popup.style.maxHeight = `${Math.min(this.maxHeight, availableHeight)}px`
            }
            if (this.maxWidth !== 'auto') {
              this.popup.style.maxWidth = `${this.maxWidth}px`
            }
            if (this.fullWidth) {
              this.popup.style.width = '100vw'
            }
          },
        }),
      ],
    })

    Object.assign(this.popup.style, {
      left: this.fullWidth ? '0px' : `${x}px`,
      top: `${y}px`,
    })
  }

  private _handleClick = (event: Event) => {
    // Prevent disabled items from being clicked
    if (this.disabled) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    const hasSubmenu = this.hasContent || this.hasNestedItems
    if (hasSubmenu) {
      // Check if click was on trigger element
      const triggerElements = this._trigger
      const path = event.composedPath()
      const isClickOnTrigger = triggerElements.some(trigger =>
        path.includes(trigger),
      )

      if (isClickOnTrigger) {
        event.preventDefault()
        event.stopPropagation()

        // If submenu was opened by hover, clicking should keep it open
        // Otherwise, toggle the submenu state
        if (this.opened && this.openedByHover) {
          // Clear the hover flag so future clicks will toggle normally
          this.openedByHover = false
        } else {
          // Normal toggle behavior
          this.opened ? this.hideDropDown() : this.showDropDown(false)
        }
      } else if (this.opened) {
        // Click is inside the open popup content (not on the trigger).
        // If a navigable element is in the composed path, it's a SPA link
        // click — close the dropdown so the menu doesn't stay open after
        // client-side navigation.
        const isNavClick = path.some(el => {
          if (!(el instanceof Element)) return false
          const tag = el.tagName.toUpperCase()
          return tag === 'A' || tag === 'DAP-DS-LINK'
        })
        if (isNavClick) {
          this.hideDropDown()
        }
      }
    } else if (this.hasLink) {
      // SPA router link — delegate click to the slotted link element
      const triggerElements = this._trigger
      const path = event.composedPath()
      const isClickOnTrigger = triggerElements.some(trigger =>
        path.includes(trigger),
      )
      if (isClickOnTrigger) {
        event.preventDefault()
        const linkEl = this._getLinkElement()
        if (linkEl) {
          const href = linkEl.getAttribute('href')
          linkEl.click()
          if (href) {
            this.emit('dds-navigation-item-click', { href, event })
          }
        }
        // Close all ancestor open navigation menu items so SPA navigation
        // doesn't leave the dropdown open after a client-side route change.
        let ancestor = this.parentElement?.closest(
          'dap-ds-navigation-menu-item',
        ) as DapDSNavigationMenuItem | null
        while (ancestor) {
          if (ancestor.opened) {
            ancestor.hideDropDown()
          }
          ancestor = ancestor.parentElement?.closest(
            'dap-ds-navigation-menu-item',
          ) as DapDSNavigationMenuItem | null
        }
      }
    } else {
      // Simple link - emit navigation event
      const triggerElements = this._trigger
      const href = triggerElements[0]?.getAttribute('href')
      if (href) {
        this.emit('dds-navigation-item-click', {
          href,
          event,
        })
      }
    }
  }

  private _handleMouseOver = (event: MouseEvent) => {
    // Close sibling items when hovering over a different item
    // This ensures only one item's submenu is open at a time on hover
    // Also cancel any pending timers to prevent race conditions when moving mouse quickly
    const parent = this.parentElement
    if (parent) {
      const siblings =
        parent.querySelectorAll(':scope > dap-ds-navigation-menu-item') || []

      siblings.forEach(item => {
        if (item !== this) {
          const siblingItem = item as DapDSNavigationMenuItem
          // Cancel any pending submenu opening timer
          siblingItem.submenuController.cancelPendingOpen()
          // Close if already open
          if (siblingItem.opened) {
            siblingItem.hideDropDown()
          }
        }
      })
    }

    event.stopPropagation()
  }

  private _handleMouseLeave = (_event: MouseEvent) => {
    // Cancel pending submenu opening timer when mouse leaves the item
    // This prevents the submenu from opening if the mouse leaves before the delay completes
    // Only cancel if the submenu is not already open
    if (!this.opened) {
      this.submenuController.cancelPendingOpen()
    }
  }

  /** Handle keyboard navigation for submenu items */
  private _handleKeyDown = (event: KeyboardEvent) => {
    // Handle navigation within THIS item's open submenu
    if (this.opened && (this.hasNestedItems || this.hasContent)) {
      const nestedItems = Array.from(
        this.querySelectorAll(':scope > dap-ds-navigation-menu-item'),
      ) as DapDSNavigationMenuItem[]

      if (nestedItems.length > 0) {
        const target = event.target as HTMLElement
        const currentIndex = nestedItems.findIndex(
          item => item.contains(target) || item === target,
        )

        // ArrowDown: move to next item in submenu
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          event.stopPropagation()
          const nextIndex =
            currentIndex < nestedItems.length - 1 ? currentIndex + 1 : 0
          nestedItems[nextIndex].focus()
          return
        }
        // ArrowUp: move to previous item in submenu
        else if (event.key === 'ArrowUp') {
          event.preventDefault()
          event.stopPropagation()
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : nestedItems.length - 1
          nestedItems[prevIndex].focus()
          return
        }
        // ArrowRight: open nested submenu if current item has one
        else if (event.key === 'ArrowRight' && currentIndex >= 0) {
          const currentItem = nestedItems[currentIndex]
          if (currentItem.hasNestedItems || currentItem.hasContent) {
            event.preventDefault()
            event.stopPropagation()
            if (!currentItem.opened) {
              currentItem.showDropDown()
              currentItem.updateComplete.then(() => {
                const firstNested = currentItem.querySelector(
                  'dap-ds-navigation-menu-item',
                ) as DapDSNavigationMenuItem
                if (firstNested) {
                  firstNested.focus()
                }
              })
            }
            return
          }
        }
        // Enter or Space: activate the current focused item
        else if (
          (event.key === 'Enter' || event.key === ' ') &&
          currentIndex >= 0
        ) {
          const currentItem = nestedItems[currentIndex]
          const trigger = currentItem.querySelector(
            '[slot="trigger"]',
          ) as HTMLElement

          // Check if trigger is a link or any interactive element, the element can be in uppercase or lowercase
          const linkTags = [
            'A',
            'DAP-DS-LINK',
            'DAP-DS-BUTTON',
            'BUTTON',
            'LINK',
          ]
          const isInteractive =
            linkTags.includes(trigger?.tagName.toLowerCase()) ||
            linkTags.includes(trigger?.tagName)

          // For links with Enter key, let the browser handle it naturally
          // Only prevent default for Space key or non-link elements
          if (!isInteractive || event.key === ' ') {
            event.preventDefault()
            event.stopPropagation()
          }

          if (currentItem.hasNestedItems || currentItem.hasContent) {
            event.preventDefault()
            event.stopPropagation()
            if (!currentItem.opened) {
              currentItem.showDropDown()
            }
          } else if (!isInteractive || event.key === ' ') {
            // For non-links or Space key, simulate click
            trigger?.click()
          }
          // For links with Enter, browser default handles navigation
          return
        }
      } else if (this.hasContent) {
        // Mega menu: navigate between focusable items with arrow keys
        const focusableItems = this._getFocusablePopupItems()
        if (focusableItems.length > 0) {
          const target = event.target as HTMLElement
          const currentIndex = focusableItems.findIndex(
            item => item === target || item.contains(target),
          )
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            event.stopPropagation()
            const nextIndex =
              currentIndex < 0
                ? 0
                : currentIndex < focusableItems.length - 1
                  ? currentIndex + 1
                  : 0
            this._focusPopupItem(focusableItems[nextIndex])
            return
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            event.stopPropagation()
            const prevIndex =
              currentIndex < 0
                ? focusableItems.length - 1
                : currentIndex > 0
                  ? currentIndex - 1
                  : focusableItems.length - 1
            this._focusPopupItem(focusableItems[prevIndex])
            return
          } else if (event.key === 'Home') {
            event.preventDefault()
            event.stopPropagation()
            this._focusPopupItem(focusableItems[0])
            return
          } else if (event.key === 'End') {
            event.preventDefault()
            event.stopPropagation()
            this._focusPopupItem(focusableItems[focusableItems.length - 1])
            return
          } else if (
            (event.key === 'Enter' || event.key === ' ') &&
            currentIndex >= 0
          ) {
            event.preventDefault()
            event.stopPropagation()
            this._activateFocusableItem(focusableItems[currentIndex])
            return
          }
        }
      }
    }

    // Handle navigation when THIS item is inside a submenu (level > 0)
    if (this.level > 0) {
      const parent = this.parentElement
      if (!parent) return

      // Get sibling items at the same level
      const siblings = Array.from(
        parent.querySelectorAll(':scope > dap-ds-navigation-menu-item'),
      ) as DapDSNavigationMenuItem[]

      if (siblings.length === 0) return

      const currentIndex = siblings.indexOf(this)

      // ArrowDown: move to next sibling
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()
        const nextIndex =
          currentIndex < siblings.length - 1 ? currentIndex + 1 : 0
        siblings[nextIndex].focus()
      }
      // ArrowUp: move to previous sibling
      else if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : siblings.length - 1
        siblings[prevIndex].focus()
      }
      // ArrowRight: open THIS item's submenu if it has one
      else if (event.key === 'ArrowRight') {
        if (this.hasNestedItems || this.hasContent) {
          event.preventDefault()
          event.stopPropagation()
          if (!this.opened) {
            this.showDropDown()
            this.updateComplete.then(() => {
              this.focusFirstDropdownChild()
            })
          } else {
            this.focusFirstDropdownChild()
          }
        }
      }
      // ArrowLeft: close parent submenu and return focus
      else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        event.stopPropagation()
        // Find the parent menu-item that contains this submenu
        const parentMenuItem = parent.closest(
          'dap-ds-navigation-menu-item',
        ) as DapDSNavigationMenuItem
        if (parentMenuItem) {
          parentMenuItem.hideDropDown()
          parentMenuItem.focus()
        }
      }
      // Escape: close parent submenu
      else if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        const parentMenuItem = parent.closest(
          'dap-ds-navigation-menu-item',
        ) as DapDSNavigationMenuItem
        if (parentMenuItem) {
          parentMenuItem.hideDropDown()
          parentMenuItem.focus()
        }
      }
      // Enter or Space: activate the link or trigger
      else if (event.key === 'Enter' || event.key === ' ') {
        const trigger = this.querySelector('[slot="trigger"]') as HTMLElement

        // Check if trigger is a link
        const linkTags = ['A', 'DAP-DS-LINK', 'LINK']
        const isLink =
          trigger &&
          (linkTags.includes(trigger.tagName.toLowerCase()) ||
            linkTags.includes(trigger.tagName))

        // For links with Enter key, let the browser handle it naturally
        // Only prevent default for Space key or non-link elements
        if (!isLink || event.key === ' ') {
          event.preventDefault()
          event.stopPropagation()
        }

        // If item has submenu, open it
        if (this.hasNestedItems || this.hasContent) {
          event.preventDefault()
          event.stopPropagation()
          if (!this.opened) {
            this.showDropDown(false)
          }
        } else if (this.hasLink) {
          // slot="link" pattern: delegate to the visually-hidden router link
          event.preventDefault()
          event.stopPropagation()
          const linkEl = this._getLinkElement()
          if (linkEl) {
            const href = linkEl.getAttribute('href')
            linkEl.click()
            if (href) {
              this.emit('dds-navigation-item-click', { href, event })
            }
          }
        } else if (!isLink || event.key === ' ') {
          // For non-links or Space key, simulate click
          trigger?.click()
        }
        // For links with Enter, browser default handles navigation
      }
    }
  }

  /** @ignore Override parent's handleDocumentMouseDown to be parent-aware for nested menus. */
  protected handleDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as HTMLElement
    const path = event.composedPath()
    const isInsideThis = path.includes(this)
    const isInsideNestedItems = this._isWithinNestedItems(target)

    if (!isInsideThis && !isInsideNestedItems) {
      this.hideDropDown()
    }
  }

  /** Check if target is within any nested navigation menu items. */
  private _isWithinNestedItems(target: HTMLElement): boolean {
    const nestedItems = Array.from(
      this.querySelectorAll('dap-ds-navigation-menu-item'),
    ) as DapDSNavigationMenuItem[]

    return nestedItems.some(item => {
      return (
        item.contains(target) ||
        (item.shadowRoot && item.shadowRoot.contains(target))
      )
    })
  }

  focus() {
    const triggerElements = this._trigger
    if (triggerElements && triggerElements.length > 0) {
      triggerElements[0].focus()
    }
  }

  /**
   * Sets this item as the active (or inactive) roving tabindex target.
   * Only the active item's trigger is in the sequential tab order (tabindex="0").
   * All others are removed (tabindex="-1") so Tab exits the whole nav in one step.
   */
  setRovingTabIndex(active: boolean) {
    if (active) {
      this.setAttribute('data-nav-current', '')
    } else {
      this.removeAttribute('data-nav-current')
    }
    this._isRovingActive = active

    const trigger = this._trigger?.[0]
    if (trigger) {
      this._applyTriggerTabIndex(trigger, active)
    } else {
      // Trigger not yet in DOM — defer until after Lit renders
      this.updateComplete.then(() => {
        const t = this._trigger?.[0]
        if (t) this._applyTriggerTabIndex(t, active)
      })
    }
  }

  /** Apply tabindex to a trigger element and its shadow-root inner focusable element. */
  private _applyTriggerTabIndex(trigger: HTMLElement, active: boolean) {
    if (trigger.shadowRoot) {
      const inner = trigger.shadowRoot.querySelector<HTMLElement>(
        'a[href], button:not([disabled])',
      )
      if (inner) {
        // For custom elements with a shadow root: manage tabindex on the inner
        // focusable element directly. The host must have NO tabindex attribute
        // (not even -1) to avoid creating a shadow-encapsulated tab scope that
        // would produce two consecutive Tab stops (host → inner button).
        // Without a tabindex on the host, inner elements participate directly
        // in the flat tree tab order.
        trigger.removeAttribute('tabindex')
        inner.setAttribute('tabindex', active ? '0' : '-1')
        return
      }
    }
    // Plain HTML element (no shadow root) or no inner focusable found:
    // use the host element as the tab stop directly.
    trigger.setAttribute('tabindex', active ? '0' : '-1')
  }

  /** Focus the first focusable element in the dropdown (supports both nested nav items and mega menu content). */
  focusFirstDropdownChild() {
    const firstNavItem = this.querySelector(
      'dap-ds-navigation-menu-item',
    ) as DapDSNavigationMenuItem | null
    if (firstNavItem) {
      firstNavItem.focus()
      return
    }
    const items = this._getFocusablePopupItems()
    if (items.length > 0) {
      this._focusPopupItem(items[0])
    }
  }

  /** Focus the last focusable element in the dropdown (supports both nested nav items and mega menu content). */
  focusLastDropdownChild() {
    const navItems = Array.from(
      this.querySelectorAll(':scope > dap-ds-navigation-menu-item'),
    ) as DapDSNavigationMenuItem[]
    if (navItems.length > 0) {
      navItems[navItems.length - 1].focus()
      return
    }
    const items = this._getFocusablePopupItems()
    if (items.length > 0) {
      this._focusPopupItem(items[items.length - 1])
    }
  }

  /** Get all top-level focusable items inside the popup slot content (for mega menu arrow navigation). */
  private _getFocusablePopupItems(): HTMLElement[] {
    if (!this.popup) return []
    const contentSlot = this.popup.querySelector(
      'slot:not([name])',
    ) as HTMLSlotElement | null
    const assigned = contentSlot?.assignedElements({ flatten: true }) ?? []
    const items: HTMLElement[] = []
    for (const el of assigned) {
      this._collectFocusableItems(el as HTMLElement, items)
    }
    return items
  }

  private _collectFocusableItems(el: HTMLElement, result: HTMLElement[]) {
    // Custom elements with shadow roots (e.g. dap-ds-button): check if they contain a focusable inner element
    if (el.shadowRoot) {
      const inner = el.shadowRoot.querySelector<HTMLElement>(
        'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])',
      )
      if (inner) {
        result.push(el)
        return
      }
    }
    // Standard focusable elements
    if (
      ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName) &&
      !el.hasAttribute('disabled') &&
      el.getAttribute('tabindex') !== '-1'
    ) {
      result.push(el)
      return
    }
    // Recurse into children
    for (const child of el.children) {
      this._collectFocusableItems(child as HTMLElement, result)
    }
  }

  private _activateFocusableItem(item: HTMLElement) {
    if (item.shadowRoot) {
      const inner = item.shadowRoot.querySelector<HTMLElement>(
        'a[href], button:not([disabled])',
      )
      if (inner) {
        inner.click()
        return
      }
    }
    item.click()
  }

  private _focusPopupItem(item: HTMLElement) {
    // For custom elements with shadow roots, focus the inner a/button directly
    if (item.shadowRoot) {
      const inner = item.shadowRoot.querySelector<HTMLElement>(
        'a[href], button:not([disabled])',
      )
      if (inner) {
        inner.focus()
        return
      }
    }
    item.focus()
  }

  async showDropDown(byHover = false) {
    const hasSubmenu = this.hasContent || this.hasNestedItems
    if (this.opened || !hasSubmenu) {
      return
    }

    this._updatePlacementForLevel()
    this.emit('dds-navigation-dropdown-open', { item: this })

    this.opened = true
    this.openedByHover = byHover
    const triggerElements = this._trigger
    triggerElements[0]?.setAttribute('aria-expanded', 'true')

    document.addEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    this._applyZIndex()
  }

  async hideDropDown() {
    if (!this.opened) {
      return
    }

    // Close all nested items first
    const nestedItems = this.querySelectorAll('dap-ds-navigation-menu-item')
    nestedItems.forEach(item => {
      if ((item as DapDSNavigationMenuItem).opened) {
        ;(item as DapDSNavigationMenuItem).hideDropDown()
      }
    })

    this.opened = false
    this.openedByHover = false
    const triggerElements = this._trigger
    triggerElements[0]?.setAttribute('aria-expanded', 'false')

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
  }

  /** Handle trigger slot change events. */
  private _handleTriggerSlotChange() {
    this._handleTriggerSlotChangeCallback()
    this.stopPositionUpdate()
    this.updateComplete.then(() => {
      if (this.triggerElement) {
        this.startPositionUpdate()
      }
    })
  }

  /** Update trigger element attributes and properties. */
  _handleTriggerSlotChangeCallback() {
    this.updateComplete.then(() => {
      const triggerElements = this._trigger
      if (triggerElements && triggerElements.length > 0) {
        this.triggerElement = triggerElements[0]

        triggerElements[0].setAttribute('aria-controls', 'dropdown')

        const hasSubmenu = this.hasContent || this.hasNestedItems
        if (hasSubmenu) {
          triggerElements[0].setAttribute(
            'aria-expanded',
            this.opened ? 'true' : 'false',
          )
          triggerElements[0].setAttribute('aria-haspopup', 'true')
        } else {
          triggerElements[0].removeAttribute('aria-expanded')
        }

        triggerElements[0].style.display = 'flex'
        this._updateActiveState()

        // Sync roving tabindex to the (possibly new) trigger element
        this._applyTriggerTabIndex(triggerElements[0], this._isRovingActive)
      }
    })
  }

  private _updateActiveState() {
    const triggerElements = this._trigger
    if (
      (!triggerElements || triggerElements.length === 0 || !this.activeHref) &&
      !this.baseHref
    ) {
      this.active = false
    } else if (this.baseHref) {
      if (this.exactHref) {
        this.active = this.baseHref === this.activeHref
      } else {
        this.active = this.activeHref?.startsWith(this.baseHref) || false
      }
    } else {
      const triggerHref = triggerElements[0]?.getAttribute('href') || this.href
      this.active = triggerHref === this.activeHref
      ;(triggerElements[0] as any).active = this.active
    }

    // Fallback: check link slot element's href (for slot="link" pattern)
    if (!this.active && this.hasLink && this.activeHref) {
      const linkEl = this._getLinkElement()
      const linkHref = linkEl?.getAttribute('href')
      if (linkHref) {
        this.active = this.exactHref
          ? linkHref === this.activeHref
          : this.activeHref.startsWith(linkHref)
      }
    }

    // Set aria-current="page" for active navigation items
    if (triggerElements && triggerElements.length > 0) {
      if (this.active) {
        triggerElements[0].setAttribute('aria-current', 'page')
      } else {
        triggerElements[0].removeAttribute('aria-current')
      }
    }
  }

  _handleContentSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement
    if (slot) {
      const childNodes = slot.assignedElements()
      this.hasContent = childNodes.length > 0
    }
    this._updateNestedItems()
  }

  /** Render expand indicator icon based on nesting level and orientation. */
  private renderExpandIndicator() {
    if (!this.hasNestedItems && !this.hasContent) return html``

    return html`
      <span class="navigation-item__indicator" part="indicator">
        <slot name="indicator">
          <dap-ds-icon
            part="indicator-icon"
            exportparts="base:indicator-icon-base,icon:indicator-icon-icon"
            name=${this._getDefaultIconName()}
            aria-hidden="true"></dap-ds-icon>
        </slot>
      </span>
    `
  }

  /** Get the default icon name based on orientation and level. */
  private _getDefaultIconName(): string {
    // For vertical menus, always use right arrow (submenus open to the right)
    // For horizontal menus, use down arrow for top-level, right arrow for nested
    if (this.orientation === 'vertical') {
      return 'arrow-right-s-line'
    } else {
      return this.level === 0 ? 'arrow-down-s-line' : 'arrow-right-s-line'
    }
  }

  renderTrigger() {
    const hasSubmenu = this.hasContent || this.hasNestedItems
    const ariaExpanded = hasSubmenu
      ? this.opened
        ? 'true'
        : 'false'
      : undefined
    // Provide an explicit aria-label so screen readers announce the item name.
    // The label text is buried through multiple slot levels (dap-ds-button >
    // dap-ds-stack > nav-item title slot), which some screen readers don't
    // fully traverse for accessible name computation.
    const triggerAriaLabel = this._titleText || this.label || undefined
    return html`
      <dap-ds-button
        variant="subtle-menu"
        tabindex=${this._isRovingActive ? '0' : '-1'}
        ?active=${this.active}
        aria-expanded=${ifDefined(ariaExpanded)}
        aria-label=${ifDefined(triggerAriaLabel)}
        href=${ifDefined(this.href)}
        target=${ifDefined(this.target)}
        rel=${ifDefined(this.rel)}
        forceRole=${ifDefined(!hasSubmenu && this.hasLink ? 'link' : undefined)}
        part="trigger"
        exportparts="base:trigger-base,content:trigger-content,high-contrast:trigger-high-contrast">
        <dap-ds-stack direction="row" exportparts="base:trigger-stack-base">
          <span part="title">
            <slot name="title" @slotchange=${this._handleTitleSlotChange}>
              ${this.hasTitleSlot ? nothing : this.label || nothing}
            </slot>
          </span>
          ${this.renderExpandIndicator()}
        </dap-ds-stack>
      </dap-ds-button>
    `
  }

  renderPopup() {
    return html`
      <slot name="trigger" @slotchange=${this._handleTriggerSlotChange}>
        ${this.renderTrigger()}
      </slot>
      <!-- Link slot: outside inert popup, visually hidden, for SPA router links -->
      <div class="navigation-item__link-wrapper" aria-hidden="true">
        <slot name="link" @slotchange=${this._handleLinkSlotChange}></slot>
      </div>
      <div
        part="popup"
        id="popup"
        ?inert=${!this.opened}
        class=${clsx('popup', `popup--${this.effectiveSize}`, {
          'popup--opened': this.opened,
          'popup--fixed': this.floatingStrategy === 'fixed',
          'popup--has-arrow': this.hasArrow,
          'popup--overflow': this.overflow,
          'popup--full-width': this.fullWidth,
        })}>
        <div part="content">
          <slot @slotchange=${this._handleContentSlotChange}></slot>
        </div>
        ${this.hasArrow
          ? html`<div
              part="arrow"
              class=${clsx('popup__arrow')}
              role="presentation"></div>`
          : ''}
      </div>
    `
  }

  render() {
    const hasSubmenu = this.hasContent || this.hasNestedItems

    return html`
      <div
        part="base"
        class=${clsx('navigation-item', {
          'navigation-item--has-content': hasSubmenu,
          'navigation-item--opened': this.opened,
          [`navigation-item--level-${this.level}`]: true,
        })}>
        ${this.renderPopup()}
      </div>
    `
  }
}
